import React from 'react';
import '../../styles/components/AsperaCustomThemingForm.scss';
import { AsperaWrapper } from '../AsperaWrapper';
import { AsperaForm } from '../AsperaForm';
import { coreHelpUrls, customThemingAdvancedKeys, getFontFamilyList } from '../../constants/constants';
import { language } from '../../services/LanguageService';
import { SubManager } from '../../classes/SubManager';
import { AsperaError } from '../../classes/AsperaError';
import { tracking } from '../../services/TrackingService';
import { observableTimeout, openNewTab, saveFile } from '../../helpers/starterHelpers';
import { Button, Toggle, getIconComponent } from '../Carbon';
import { AsperaContentSwitcher } from '../AsperaContentSwitcher';
import { notification } from '../../services/NotificationService';
import { modal } from '../../services/ModalService';
import { themingExampleButton, themingExampleHeader, themingExampleLeftNav, themingExampleLoginPages } from '../../constants/data';
/**
 * React Component
 *
 * The AsperaCustomTheming renders custom theming form. Load directly on component.
 *
 * @example
 * ```javascript
 * <AsperaCustomThemingForm onSubmit={() => {}} />
 * ```
 */
export class AsperaCustomThemingForm extends React.Component {
    constructor() {
        super(...arguments);
        this.state = {
            loading: true,
            error: false,
            currentTheme: 'g10',
            showDesignTokens: false,
        };
        this.subManager = new SubManager();
        this.localModel = {
            g10: {},
            g100: {},
            disableLightDarkMode: true,
        };
        this.testColors = () => {
            const { currentTheme } = this.state;
            this.verifyLocalModel();
            if (!this.disableMultipleThemes && currentTheme !== tracking.currentTheme) {
                tracking.currentTheme = currentTheme;
            }
            tracking.currentCustomTheme = this.localModel;
        };
        this.save = () => {
            const { onSubmit } = this.props;
            this.verifyLocalModel();
            onSubmit(this.localModel);
            this.setState({});
        };
        this.reset = () => {
            this.localModel = {
                g10: {},
                g100: {},
                disableLightDarkMode: false,
            };
            this.save();
        };
        this.verifyLocalModel = (forceTheme) => {
            const { currentTheme } = this.state;
            // If only one theme is in use we set it to both. However, cannot break reference of object.
            if (this.disableMultipleThemes) {
                const currentModel = this.localModel[forceTheme || currentTheme];
                Object.keys(currentModel).forEach(key => {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    this.localModel.g10[key] = currentModel[key];
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    this.localModel.g100[key] = currentModel[key];
                });
            }
        };
        this.seeDocumentation = () => {
            openNewTab(coreHelpUrls.carbonColorTokens, true);
        };
        this.importThemeCallback = (event) => {
            const target = event === null || event === void 0 ? void 0 : event.target;
            if (event && target && target.files && target.files.length) {
                const file = target.files[0];
                const reader = new FileReader();
                reader.readAsText(file);
                reader.onload = () => {
                    try {
                        const newTheme = JSON.parse(reader.result);
                        this.setState({ loading: true });
                        if (typeof newTheme.g10 === 'object' && typeof newTheme.g100 === 'object') {
                            this.localModel = newTheme;
                            this.save();
                        }
                        else {
                            notification.create('error', language.get('customThemeForm.unableImport'), language.get('customThemeForm.invalidJson'));
                        }
                        this.subManager.setSubscription(observableTimeout().subscribe({
                            next: () => {
                                this.setState({ loading: false });
                            },
                            error: () => {
                                this.setState({ loading: false });
                            },
                        }));
                    }
                    catch (error) {
                        new AsperaError('AsperaCustomThemingForm: Unable to parse import JSON', error);
                        notification.create('error', language.get('customThemeForm.unableImport'));
                        this.setState({ loading: false });
                    }
                };
                reader.onerror = error => {
                    new AsperaError('AsperaCustomThemingForm: Unable to parse import', error);
                    notification.create('error', language.get('customThemeForm.unableImport'));
                };
            }
        };
        this.importTheme = () => {
            const trigger = document.querySelector('#import-theme-trigger');
            if (trigger) {
                trigger.click();
            }
            else {
                new AsperaError('AsperaCustomThemingForm: unable to trigger import flow');
            }
        };
        this.exportTheme = () => {
            saveFile(this.localModel, 'text/plain', 'aspera-custom-theme.json');
        };
        this.getLegendButton = (image, items) => {
            const openLegendModal = () => {
                const modalInfo = modal.prepare();
                const modalProps = {
                    modalHeading: language.get('customThemeForm.legend'),
                    passiveModal: true,
                };
                const modalBody = (React.createElement("div", { className: "aspera-custom-theming-form--modal-legend" },
                    React.createElement("img", { className: "legend-pointer-image", src: image, alt: language.get('customThemeForm.legend') }),
                    items.map((item, index) => {
                        return (React.createElement("div", { className: "legend-pointer-item", key: index },
                            React.createElement("span", { className: "legend-pointer-number", style: { backgroundColor: '#D12771' } }, index + 1),
                            React.createElement("span", { className: "legend-pointer-item" }, item)));
                    })));
                this.subManager.setSubscription(modal.create(modalInfo.id, modalProps, modalBody).subscribe({
                    next: () => { },
                    error: () => { },
                }));
            };
            return React.createElement(Button, { size: "sm", kind: "ghost", className: "theming-secondary-button", onClick: openLegendModal }, language.get('customThemeForm.seeLegend'));
        };
    }
    get disableMultipleThemes() {
        const { disableMultipleThemes } = this.props;
        return !!(this.localModel.disableLightDarkMode || disableMultipleThemes);
    }
    get randomColor() {
        return `#${Math.floor(Math.random() * 16777215).toString(16)}`;
    }
    get switcherItems() {
        return [
            {
                name: 'LIGHT',
                text: language.get('customThemeForm.lightTheme'),
                onClick: () => {
                    this.setState({ currentTheme: 'g10', loading: true });
                    this.subManager.setSubscription(observableTimeout().subscribe({
                        next: () => {
                            this.setState({ loading: false });
                        },
                        error: () => {
                            this.setState({ loading: false });
                        },
                    }));
                },
            },
            {
                name: 'DARK',
                text: language.get('customThemeForm.darkTheme'),
                onClick: () => {
                    this.setState({ currentTheme: 'g100', loading: true });
                    this.subManager.setSubscription(observableTimeout().subscribe({
                        next: () => {
                            this.setState({ loading: false });
                        },
                        error: () => {
                            this.setState({ loading: false });
                        },
                    }));
                },
            },
        ];
    }
    get previewButton() {
        return React.createElement(Button, { size: "sm", kind: "primary", className: "theming-preview-button", renderIcon: getIconComponent('View'), iconDescription: language.get('customThemeForm.preview'), onClick: this.testColors }, language.get('customThemeForm.preview'));
    }
    get resetImportExport() {
        return React.createElement(React.Fragment, null,
            React.createElement(Button, { size: "sm", kind: "tertiary", className: "theming-import-export-button", onClick: this.reset }, language.get('customThemeForm.setToDefault')),
            React.createElement(Button, { size: "sm", kind: "tertiary", className: "theming-import-export-button", onClick: this.exportTheme }, language.get('customThemeForm.export')),
            React.createElement(Button, { size: "sm", kind: "tertiary", className: "theming-import-export-button", onClick: this.importTheme }, language.get('customThemeForm.import')),
            React.createElement("input", { type: "file", id: "import-theme-trigger", accept: ".json", onChange: this.importThemeCallback }));
    }
    get formDetails() {
        const { currentTheme, showDesignTokens } = this.state;
        const { disableMultipleThemes, disableNewLoginPageFeatures } = this.props;
        return {
            model: currentTheme === 'g10' ? this.localModel.g10 : this.localModel.g100,
            id: 'aspera-form-custom-theming-form-container',
            onSubmit: this.save,
            items: [
                { type: 'header', label: language.get('customThemeForm.themeEditor'), helperText: language.get('customThemeForm.themeEditorHelper') },
                { type: 'custom', customView: this.resetImportExport },
                { type: 'header', label: language.get('customThemeForm.themes'), helperText: language.get('customThemeForm.themeHelper'), hidden: () => !!disableMultipleThemes },
                {
                    type: 'custom',
                    customView: (React.createElement(React.Fragment, null,
                        React.createElement(Toggle, { id: "custom-theme-toggle-multiple-themes", toggled: !this.localModel.disableLightDarkMode, labelText: language.get('customThemeForm.enableLightDarkThemes'), "data-auto": "form-item-toggle-disableLightDarkMode", onToggle: (checked) => {
                                this.localModel.disableLightDarkMode = !checked;
                                if (this.localModel.disableLightDarkMode) {
                                    this.verifyLocalModel('g10');
                                }
                                this.setState({ currentTheme: 'g10' });
                            }, labelA: language.get('forms.off'), labelB: language.get('forms.on') }),
                        !this.localModel.disableLightDarkMode && React.createElement(AsperaContentSwitcher, { className: "content-switcher", items: this.switcherItems, selectedIndex: currentTheme === 'g10' ? 0 : 1 }))),
                    hidden: () => !!disableMultipleThemes,
                },
                { type: 'header', label: language.get('customThemeForm.font'), helperText: language.get('customThemeForm.fontHelper') },
                { type: 'custom', customView: this.previewButton },
                { type: 'select', pointer: 'typographyFontFamily', label: language.get('customThemeForm.font'), selectDefaultText: language.get('customThemeForm.defaultFont'), items: getFontFamilyList() },
                { type: 'header', label: language.get('customThemeForm.loginPages'), hidden: disableNewLoginPageFeatures },
                {
                    type: 'custom',
                    hidden: disableNewLoginPageFeatures,
                    customView: this.getLegendButton(themingExampleLoginPages, [language.get('customThemeForm.loginPanelBackgroundColor'), language.get('customThemeForm.loginPanelTextColor'), language.get('customThemeForm.loginInPanelAnnouncementBackgroundColor'), language.get('customThemeForm.loginInPanelAnnouncementTextColor'), language.get('customThemeForm.loginAnnouncementBackgroundColor'), language.get('customThemeForm.loginAnnouncementBackgroundTextColor')]),
                },
                { type: 'color', hidden: disableNewLoginPageFeatures, pointer: 'loginPanelBackgroundColor', label: language.get('customThemeForm.loginPanelBackgroundColor') },
                { type: 'color', hidden: disableNewLoginPageFeatures, pointer: 'loginPanelTextColor', label: language.get('customThemeForm.loginPanelTextColor') },
                { type: 'color', hidden: disableNewLoginPageFeatures, pointer: 'loginInPanelAnnouncementBackgroundColor', label: language.get('customThemeForm.loginInPanelAnnouncementBackgroundColor') },
                { type: 'color', hidden: disableNewLoginPageFeatures, pointer: 'loginInPanelAnnouncementTextColor', label: language.get('customThemeForm.loginInPanelAnnouncementTextColor') },
                { type: 'color', hidden: disableNewLoginPageFeatures, pointer: 'loginAnnouncementBackgroundColor', label: language.get('customThemeForm.loginAnnouncementBackgroundColor') },
                { type: 'color', hidden: disableNewLoginPageFeatures, pointer: 'loginAnnouncementBackgroundTextColor', label: language.get('customThemeForm.loginAnnouncementBackgroundTextColor') },
                { type: 'header', label: language.get('customThemeForm.header') },
                {
                    type: 'custom',
                    customView: (React.createElement(React.Fragment, null,
                        this.previewButton,
                        this.getLegendButton(themingExampleHeader, [language.get('customThemeForm.headerBackground'), language.get('customThemeForm.headerTextIcon'), language.get('customThemeForm.headerAccentBorders')]))),
                },
                { type: 'color', pointer: 'headerBackground', label: language.get('customThemeForm.headerBackground') },
                { type: 'color', pointer: 'headerTextIcon', label: language.get('customThemeForm.headerTextIcon') },
                { type: 'color', pointer: 'headerAccentBorders', label: language.get('customThemeForm.headerAccentBorders') },
                { type: 'color', pointer: 'headerActiveItemBackground', label: language.get('customThemeForm.headerActiveItemBackground') },
                { type: 'color', pointer: 'headerActiveItemColor', label: language.get('customThemeForm.headerActiveItemColor') },
                { type: 'color', pointer: 'headerHoverItemBackground', label: language.get('customThemeForm.headerItemHoverBackground') },
                { type: 'header', label: language.get('customThemeForm.leftNav') },
                {
                    type: 'custom',
                    customView: (React.createElement(React.Fragment, null,
                        this.previewButton,
                        this.getLegendButton(themingExampleLeftNav, [language.get('customThemeForm.leftNavBackground'), language.get('customThemeForm.leftNavTextColor'), language.get('customThemeForm.leftNavItemActiveBackground'), language.get('customThemeForm.leftNavItemActiveBackgroundAccent')]))),
                },
                { type: 'color', pointer: 'leftNavBackground', label: language.get('customThemeForm.leftNavBackground') },
                { type: 'color', pointer: 'leftNavTextColor', label: language.get('customThemeForm.leftNavTextColor') },
                { type: 'color', pointer: 'leftNavActiveItemBackground', label: language.get('customThemeForm.leftNavItemActiveBackground') },
                { type: 'color', pointer: 'leftNavActiveItemAccent', label: language.get('customThemeForm.leftNavItemActiveBackgroundAccent') },
                { type: 'color', pointer: 'leftNavActiveItemHover', label: language.get('customThemeForm.leftNavItemHoverBackground') },
                { type: 'header', label: language.get('customThemeForm.buttons'), helperText: language.get('customThemeForm.buttonsHelper') },
                {
                    type: 'custom',
                    customView: (React.createElement(React.Fragment, null,
                        this.previewButton,
                        this.getLegendButton(themingExampleButton, [language.get('customThemeForm.primaryButton'), language.get('customThemeForm.secondaryButton'), language.get('customThemeForm.tertiaryButton')]))),
                },
                { type: 'color', pointer: 'buttonPrimaryBackground', label: language.get('customThemeForm.buttonPrimaryBackground') },
                { type: 'color', pointer: 'buttonPrimaryText', label: language.get('customThemeForm.buttonPrimaryText') },
                { type: 'color', pointer: 'buttonPrimaryBackgroundHover', label: language.get('customThemeForm.buttonPrimaryHoverBackground') },
                { type: 'color', pointer: 'buttonPrimaryBackgroundActive', label: language.get('customThemeForm.buttonPrimaryActiveBackground') },
                { type: 'color', pointer: 'buttonSecondaryBackground', label: language.get('customThemeForm.buttonSecondaryBackground') },
                { type: 'color', pointer: 'buttonSecondaryText', label: language.get('customThemeForm.buttonSecondaryText') },
                { type: 'color', pointer: 'buttonSecondaryBackgroundHover', label: language.get('customThemeForm.buttonSecondaryHoverBackground') },
                { type: 'color', pointer: 'buttonSecondaryBackgroundActive', label: language.get('customThemeForm.buttonSecondaryActiveBackground') },
                { type: 'color', pointer: 'buttonTertiaryBorder', label: language.get('customThemeForm.buttonTertiaryBorder') },
                { type: 'color', pointer: 'buttonTertiaryText', label: language.get('customThemeForm.buttonTertiaryText') },
                { type: 'color', pointer: 'buttonTertiaryTextHoverActive', label: language.get('customThemeForm.buttonTertiaryTextStates') },
                { type: 'color', pointer: 'buttonTertiaryBackgroundHover', label: language.get('customThemeForm.buttonTertiaryHoverBackground') },
                { type: 'color', pointer: 'buttonTertiaryBackgroundActive', label: language.get('customThemeForm.buttonTertiaryActiveBackground') },
                { type: 'header', label: language.get('customThemeForm.advanced'), helperText: language.get('customThemeForm.advancedHelper') },
                {
                    type: 'custom',
                    customView: React.createElement(Button, { size: "sm", kind: "ghost", className: "theming-secondary-button", onClick: () => this.setState({ showDesignTokens: !showDesignTokens }) }, language.get(`customThemeForm.${showDesignTokens ? 'hideDesignTokens' : 'showDesignTokens'}`)),
                },
                {
                    type: 'custom',
                    hidden: !showDesignTokens,
                    customView: (React.createElement(React.Fragment, null,
                        this.previewButton,
                        React.createElement(Button, { size: "sm", kind: "ghost", className: "theming-secondary-button", onClick: this.seeDocumentation }, language.get('customThemeForm.seeDocumentation')))),
                },
                ...customThemingAdvancedKeys.map(key => {
                    return { type: 'color', hidden: !showDesignTokens, pointer: key, label: key.replace('$', '') };
                }),
            ],
        };
    }
    componentWillUnmount() {
        tracking.currentCustomTheme = this.themeOnLoad;
        this.subManager.unsubscribeAll();
    }
    componentDidMount() {
        const { value, disableMultipleThemes, requestRandomColors } = this.props;
        this.localModel = value;
        this.themeOnLoad = tracking.currentCustomTheme;
        if (disableMultipleThemes) {
            this.localModel.disableLightDarkMode = true;
        }
        if (requestRandomColors) {
            this.subManager.setSubscription(requestRandomColors.subscribe({
                next: value => {
                    const { currentTheme } = this.state;
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    const items = {};
                    if (value) {
                        this.formDetails.items.forEach(item => {
                            if (item.pointer) {
                                items[item.pointer] = this.randomColor;
                            }
                        });
                    }
                    else {
                        this.formDetails.items.forEach(item => {
                            if (item.pointer && !item.pointer.includes('$')) {
                                items[item.pointer] = this.randomColor;
                            }
                        });
                    }
                    this.localModel[currentTheme] = items;
                    this.testColors();
                    this.setState({});
                },
                error: error => {
                    new AsperaError('AsperaCustomThemingForm: unable to subscribe for randomizing', error);
                },
            }));
        }
        this.setState({ loading: false, currentTheme: tracking.currentTheme });
    }
    render() {
        const { className } = this.props;
        const { loading, error } = this.state;
        const id = 'aspera-form-custom-theming-form';
        return (React.createElement(AsperaWrapper, { className: `aspera-custom-theming-form ${className ? className : ''}`, loading: loading, error: error, id: id, disableWrapping: true },
            React.createElement(AsperaForm, { formDetails: this.formDetails, fullPageWrapperId: id })));
    }
}
